import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Eye, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useDialerGroups, useDialers } from '../api';
import type { DialerGroupSearchRow } from '../types';
import { useModuleFilter } from '@/lib/permissions/use-module-filter';
import { MODULE_LISTING_KEYS, BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';

export function SearchDialerPage() {
  const dialers = useDialers();
  const filteredDialers = useModuleFilter(dialers.data ?? [], MODULE_LISTING_KEYS.MANAGE_DIALERS, (d) => d.dialerId);
  const [pickedDialerId, setPickedDialerId] = useState<string>('');
  const [searchedDialerId, setSearchedDialerId] = useState<number | null>(null);
  const [groupModal, setGroupModal] = useState<{ groupName: string } | null>(null);

  const groupsQuery = useDialerGroups(searchedDialerId);
  const rows = groupsQuery.data ?? [];

  const columns = useMemo<ColumnDef<DialerGroupSearchRow>[]>(
    () => [
      { accessorKey: 'dialerName', header: 'Dialer name' },
      {
        accessorKey: 'groupName',
        header: 'Group',
        cell: ({ row }) => (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setGroupModal({ groupName: row.original.groupName })}
          >
            <Eye className="h-4 w-4" />
            View group
          </Button>
        ),
      },
    ],
    [],
  );

  const canSearch = usePermission('Manage Dialers.Search Dialer', BASE_PERMISSION.VIEW);

  if (!canSearch) return <Navigate to="/dialer-list" replace />;

  const handleSearch = () => {
    if (!pickedDialerId) return;
    setSearchedDialerId(Number(pickedDialerId));
  };

  return (
    <FillPage>
      <PageHeader
        title="Search Dialers"
        description="Look up which groups a particular dialer currently serves."
        actions={
          <Button asChild variant="outline">
            <Link to="/dialer-list">
              <ArrowLeft className="h-4 w-4" />
              Back to dialers
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Pick a dialer</CardTitle>
          <CardDescription>
            Choose a dialer to see every group it&apos;s attached to.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-end">
            <div className="flex-1 space-y-1.5">
              <label
                htmlFor="dialer-picker"
                className="text-sm font-medium leading-none"
              >
                Dialer
              </label>
              <Select value={pickedDialerId} onValueChange={setPickedDialerId}>
                <SelectTrigger id="dialer-picker">
                  <SelectValue
                    placeholder={
                      dialers.isLoading ? 'Loading dialers…' : 'Pick a dialer'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {filteredDialers.map((d) => (
                    <SelectItem key={d.dialerId} value={String(d.dialerId)}>
                      {d.dialerName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleSearch}
              disabled={!pickedDialerId || groupsQuery.isFetching}
              className="sm:mb-0"
            >
              {groupsQuery.isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <DataTable<DialerGroupSearchRow, unknown>
        tableId="dialers:search:v1"
        columns={columns}
        data={rows}
        isLoading={groupsQuery.isFetching}
        searchPlaceholder="Filter results…"
        emptyTitle={
          searchedDialerId === null ? 'Pick a dialer to begin' : 'No groups found'
        }
        emptyDescription={
          searchedDialerId === null
            ? 'Choose a dialer from the dropdown above and click Search.'
            : 'This dialer isn’t attached to any group.'
        }
        getRowId={(row, idx) => `${row.dialerName}:${row.groupName}:${idx}`}
        exportFilename="dialer-groups.csv"
        pageSize={10}
      />

      <Dialog
        open={groupModal !== null}
        onOpenChange={(o) => !o && setGroupModal(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Group details</DialogTitle>
            <DialogDescription>
              Each comma-separated entry from the legacy payload is shown on its own
              line.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs">
            {(groupModal?.groupName ?? '').split(',').map((part, i) => (
              <div
                key={i}
                className="break-all border-b border-border/60 pb-2 last:border-0 last:pb-0"
              >
                {part.trim() || <span className="text-muted-foreground">(empty)</span>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
}
